/* eslint-disable */
import mongoose, { Schema } from 'mongoose';
import {
  always,
  applySpec,
  complement,
  compose,
  equals,
  hasIn,
  identity,
  prop,
  reject,
  toLower,
  __,
} from 'ramda';
import uuid from 'uuid/v4';
import { async as asyncUtils, string as stringUtils } from '@555platform/utility-belt.js';
import { crypto as cryptoUtils, renameObjectKeys } from '../util';
import { query as queryModel } from './util';
import idmuserinfo from './idmuserinfo';

const IdmuserinfoModel = idmuserinfo;

export const COLLECTION_NAME = 'Application';

const now = () => new Date();

const ApplicationSchema = new Schema({
  className: {
    type: String,
    default: 'Application',
  },
  readAccess: [String],
  writeAccess: [String],
  ownerID: {
    type: String,
    default: '',
  },
  createdBy: {
    type: String,
    default: '',
  },
  createAt: {
    type: Date,
    default: now,
  },
  updatedAt: {
    type: Date,
    default: now,
  },
  fields: {
    applicationName: {
      type: String,
      default: '',
    },
    applicationID: {
      type: String,
      default: () => uuid(),
    },
    developerID: {
      type: String,
      default: '',
    },
    jwtTokenExpiration: {
      type: String,
      default: '240',
    },
    jwtServerTokenExpiration: {
      type: String,
      default: '129600',
    },
    clientTokenScopes: {
      type: String,
      default: 'client read:idm readwrite:em readwrite:ntm',
    },
    serverTokenScopes: {
      type: String,
      default: 'client readwrite:idm readwrite:em readwrite:ntm',
    },
    clientID: {
      type: String,
      default: '',
    },
    clientSecret: {
      type: String,
      default: '',
    },
    privateKey: {
      type: String,
      default: '',
    },
    publicKey: {
      type: String,
      default: '',
    },
    domain: {
      type: String,
      default: '',
    },
    federationType: {
      type: String,
      default: 'none',
    },
    version: {
      type: String,
      default: '',
    },
    appType: {
      type: String,
      default: 'master',
    },
    childAppName: {
      type: String,
      default: '',
    },
    jwtOAuth2TokenExpiration: {
      type: String,
      default: '3600',
    },
    oauth2TokenScopes: {
      type: String,
      default: 'client read:idm readwrite:em readwrite:ntm',
    },
    oauth2FriendlyName: {
      type: String,
      default: '',
    },
    oauth2RedirectURI: {
      type: String,
      default: '',
    },
    jwtOAuth2ServerTokenExpiration: {
      type: String,
      default: '86400',
    },
    oauth2ServerTokenScopes: {
      type: String,
      default: 'client readwrite:idm readwrite:em readwrite:ntm',
    },
    oauth2SelfProvisionDisabled: {
      type: Boolean,
      default: false,
    },
    oauth2EmailVerificationEnabled: {
      type: Boolean,
      default: false,
    },
    oauth2EmailVerificationGracePeriod: {
      type: Number,
      default: 60,
    },
    oauth2EmailVerificationExpiration: {
      type: Number,
      default: 0,
    },
    oauth2ShouldValidateEmail: {
      type: Boolean,
      default: false,
    },
    oauth2Providers: {
      type: [JSON],
      default: [],
    },
  },
});

const applyFieldScope = stringUtils.createTemplate('fields.${}');

// NOTE: AUM service will ensure indexeds
export const ApplicationModel = () =>
  mongoose.model(toLower(COLLECTION_NAME), ApplicationSchema, COLLECTION_NAME);

export const query = queryModel(ApplicationModel);

ApplicationSchema.statics.findApplications = (domain, skip, limit, owner) => {
  const Application = ApplicationModel();
  let apps;
  let appCount;
  const findWithOwner = Application.find({
    'fields.domain': { $regex: `.*${domain}.*` },
    ownerID: `${owner}`,
  });
  const findWithoutOwner = Application.find({ 'fields.domain': { $regex: `.*${domain}.*` } });
  if (owner !== 'allApplications') {
    apps = Application.find({ 'fields.domain': { $regex: `.*${domain}.*` }, ownerID: `${owner}` })
      .sort('fields.domain')
      .limit(limit)
      .skip(skip);
    appCount = findWithOwner.countDocuments();
  } else {
    apps = Application.find({ 'fields.domain': { $regex: `.*${domain}.*` } })
      .sort('fields.domain')
      .limit(limit)
      .skip(skip);
    appCount = findWithoutOwner.countDocuments();
  }
  return Promise.all([apps, appCount]).then(values => ({
    applications: values[0],
    count: values[1],
  }));
};

ApplicationSchema.statics.findAllApplications = (domain, skip, limit, owner) => {
  const Application = ApplicationModel();
  if (owner !== 'allApplications') {
    const apps = Application.find({
      'fields.domain': { $regex: `.*${domain}.*` },
      ownerID: `${owner}`,
    })
      .sort('fields.domain')
      .limit(limit)
      .skip(skip);
    const appCount = Application.find({
      'fields.domain': { $regex: `.*${domain}.*` },
      ownerID: `${owner}`,
    }).countDocuments();
    return Promise.all([apps, appCount]).then(values => ({
      applications: values[0],
      count: values[1],
    }));
  }
  const apps = Application.find({ 'fields.domain': { $regex: `.*${domain}.*` } })
    .sort('fields.domain')
    .limit(limit)
    .skip(skip);
  const appCount = Application.find({
    'fields.domain': { $regex: `.*${domain}.*` },
  }).countDocuments();
  return Promise.all([apps, appCount]).then(values => ({
    applications: values[0],
    count: values[1],
  }));
};

ApplicationSchema.statics.findApplicationsCount = filter => {
  const Application = ApplicationModel();
  return Application.find({
    'fields.domain': { $regex: `.*${filter}.*` },
  }).countDocuments();
};

ApplicationSchema.statics.findAllApplicationsCount = (filter, owner) => {
  const Application = ApplicationModel();
  if (owner !== 'allApplications') {
    return Application.countDocuments({
      'fields.domain': { $regex: `.*${filter}.*` },
      ownerID: `${owner}`,
    });
  }
  return Application.countDocuments({
    'fields.domain': { $regex: `.*${filter}.*` },
  });
};

ApplicationSchema.statics.deleteApplication = domain => {
  const Application = ApplicationModel();
  return Application.findOneAndRemove({ 'fields.domain': domain });
};

ApplicationSchema.statics.findApplicationByDomain = domain => {
  const Application = ApplicationModel();
  return Application.findOne({ 'fields.domain': domain });
};

ApplicationSchema.statics.findApplicationByOwnerId = ownerId => {
  const Application = ApplicationModel();
  return Application.find({ ownerID: ownerId });
};

// ApplicationSchema.statics.findApplicationByAppId = (appId) => {
//   const Application = ApplicationModel();
//   return Application.find({ 'fields.applicationID': appId });
// };

ApplicationSchema.statics.changeApplicationOwner = (id, newOwnerID) => {
  const Application = ApplicationModel();
  return Application.findOneAndUpdate(
    { _id: id },
    { ownerID: newOwnerID },
    {
      new: true,
      overwrite: false,
      upsert: false,
      strict: false,
    },
  );
};

ApplicationSchema.statics.updateOAuth2SettingsById = (id, friendlyName) => {
  const Application = ApplicationModel();
  return Application.findOneAndUpdate(
    { 'fields.applicationID': id },
    { $set: { 'fields.oauth2FriendlyName': friendlyName } },
    {
      new: true,
      overwrite: false,
      upsert: false,
      strict: false,
    },
  );
};

ApplicationSchema.statics.updateOAuth2RedirectsById = (id, redirectURIs) => {
  const Application = ApplicationModel();
  return Application.findOneAndUpdate(
    { 'fields.applicationID': id },
    { $set: { 'fields.oauth2RedirectURI': redirectURIs } },
    {
      new: true,
      overwrite: false,
      upsert: false,
      strict: false,
    },
  );
};

ApplicationSchema.statics.updateOAuth2TokenExpirationById = (id, expirationTime) => {
  const Application = ApplicationModel();
  return Application.findOneAndUpdate(
    { 'fields.applicationID': id },
    { $set: { 'fields.jwtOAuth2TokenExpiration': expirationTime } },
    {
      new: true,
      overwrite: false,
      upsert: false,
      strict: false,
    },
  );
};

ApplicationSchema.statics.updateOAuth2TokenScopesById = (id, tokenScopes) => {
  const Application = ApplicationModel();
  return Application.findOneAndUpdate(
    { 'fields.applicationID': id },
    { $set: { 'fields.oauth2TokenScopes': tokenScopes } },
    {
      new: true,
      overwrite: false,
      upsert: false,
      strict: false,
    },
  );
};

ApplicationSchema.statics.updateOAuth2ServerTokenExpirationById = (id, expirationTime) => {
  const Application = ApplicationModel();
  return Application.findOneAndUpdate(
    { 'fields.applicationID': id },
    { $set: { 'fields.jwtOAuth2ServerTokenExpiration': expirationTime } },
    {
      new: true,
      overwrite: false,
      upsert: false,
      strict: false,
    },
  );
};

ApplicationSchema.statics.updateOAuth2ServerTokenScopesById = (id, tokenScopes) => {
  const Application = ApplicationModel();
  return Application.findOneAndUpdate(
    { 'fields.applicationID': id },
    { $set: { 'fields.oauth2ServerTokenScopes': tokenScopes } },
    {
      new: true,
      overwrite: false,
      upsert: false,
      strict: false,
    },
  );
};

ApplicationSchema.statics.findApplicationByAppID = compose(
  query('findOne'),
  applySpec({ 'fields.applicationID': identity }),
);

ApplicationSchema.statics.search = (constraints, filter = '', userId, skip, limit) => {
  const Idmuserinfo = IdmuserinfoModel();
  return Idmuserinfo.aggregate([
    { $match: { user_id: userId } },
    {
      $project: {
        user_id: 1,
        teams: {
          $map: {
            input: '$auminfo.roles',
            as: 'role',
            in: { $arrayElemAt: [{ $split: ['$$role', ':'] }, 0] },
          },
        },
      },
    },
    {
      $project: {
        owners: {
          $setUnion: [['$user_id'], '$teams'],
        },
      },
    },
    { $unwind: '$owners' },
    {
      $lookup: {
        from: COLLECTION_NAME,
        localField: 'owners',
        foreignField: 'ownerID',
        as: 'applications',
      },
    },
    { $unwind: '$applications' },
    { $replaceRoot: { newRoot: '$applications' } },
    { $match: renameObjectKeys(applyFieldScope, constraints) },
    { $match: { 'fields.domain': { $regex: `.*${filter}.*` } } },
    { $skip: skip },
    { $limit: limit },
  ]);
};

ApplicationSchema.statics.findUserApplicationCount = (filter, userId) => {
  const Idmuserinfo = IdmuserinfoModel();

  return Idmuserinfo.aggregate([
    { $match: { user_id: userId } },
    {
      $project: {
        user_id: 1,
        teams: {
          $map: {
            input: '$auminfo.roles',
            as: 'role',
            in: { $arrayElemAt: [{ $split: ['$$role', ':'] }, 0] },
          },
        },
      },
    },
    {
      $lookup: {
        from: COLLECTION_NAME,
        localField: 'user_id',
        foreignField: 'ownerID',
        as: 'applications',
      },
    },
    { $unwind: '$applications' },
    { $replaceRoot: { newRoot: '$applications' } },
    { $match: { 'fields.domain': { $regex: `.*${filter}.*` } } },
    { $count: 'applicationCount' },
  ]);
};

const buildUpsertQuery = (rawUpsert, ownerID) => ({
  [applyFieldScope('applicationID')]: hasIn('applicationID', rawUpsert)
    ? prop('applicationID', rawUpsert)
    : uuid(),
  ownerID,
});

const buildUpsert = async (rawUpsert, ownerID) => {
  const filteredUpsert = reject(equals(rawUpsert.applicationID), rawUpsert);
  const fieldScopedUpsert = renameObjectKeys(applyFieldScope, filteredUpsert);
  const $set = { ...fieldScopedUpsert, updatedAt: now() };
  const $setOnInsert = { ownerID, createdAt: now(), createdBy: ownerID };
  const missing = complement(hasIn(__, rawUpsert));

  if (missing('applicationID')) {
    $setOnInsert[applyFieldScope('applicationID')] = uuid();

    if (missing('clientID')) {
      $setOnInsert[applyFieldScope('clientID')] = await cryptoUtils.generateClientId();
    }

    if (missing('clientSecret')) {
      $setOnInsert[applyFieldScope('clientSecret')] = await cryptoUtils.generateClientSecret();
    }

    if (missing('publicKey') || missing('privateKey')) {
      const { publicKey, privateKey } = await cryptoUtils.generateKeyPair();
      $setOnInsert[applyFieldScope('publicKey')] = publicKey;
      $setOnInsert[applyFieldScope('privateKey')] = privateKey;
    }

    if (missing('oauth2Providers')) {
      $setOnInsert[applyFieldScope('oauth2Providers')] = [
        {
          type: 'email',
          clientID: '',
          clientSecret: '',
        },
      ];
    }
  }

  return {
    $set,
    $setOnInsert,
  };
};

const upsertOptions = {
  upsert: true,
  setDefaultsOnInsert: true,
  new: true,
};

ApplicationSchema.statics.upsert = asyncUtils.converge(query('findOneAndUpdate'), [
  buildUpsertQuery,
  buildUpsert,
  always(upsertOptions),
]);

export default ApplicationModel;
